import RecordedChampion from '../../components/RecordedChampion.jsx'
import SchoolTeamCoverage from '../bowl/SchoolTeamCoverage.jsx'
import { useMemo, useState } from 'react'
import { stateName, NATIONAL } from '../../lib/usStates.js'
import { ownerName, formatDate, pursuitMeta } from '../../lib/championships.js'
import { championName, championList, championField, championState, fieldEmphasis } from '../../lib/championState.js'
import styles from './StatePanel.module.css'

/**
 * THE MAP AS A WORK SURFACE. Brian: "when a state is selected I should be able to
 * see what championships there are, not just a map that shows pretty colors."
 *
 * Selecting a state used to set a FILTER and nothing else — the map view rendered
 * the map alone, so the records behind a fill were unreachable without leaving
 * the map. This is the list, and every row opens its record.
 *
 * ⚠️ GROUPED BY GOVERNING BODY, BECAUSE A STATE IS NOT ONE JURISDICTION. Texas is
 * UIL and TAPPS and SPC; Florida is FHSAA, FIHSAA, SSAC and FCAPPS. The private
 * associations are the half that gets missed, and a flat state list hides them
 * inside a single number. `sr_governing_body` has been on the wire all along and
 * was read nowhere until this release.
 *
 * ⚠️ THE COUNT RECONCILES, OUT LOUD. Every record in the state is in exactly one
 * group, and the panel says so. A list that can silently drop a row is how a rep
 * stops trusting the board.
 *
 * ⚠️ NOTHING IS FILTERED OUT FOR BEING INCOMPLETE. Brian's standing rule: "There
 * cannot be any invisible sports." A record with no sport, no body or no date
 * renders in place, flagged, and is counted — never excluded for the field that
 * happens to be blank.
 *
 * ⚠️ NOTHING IS SHORTENED. Full championship names, full body names. The CSS
 * wraps; it does not ellipsis.
 */

const UNSTATED = '(no governing body recorded)'

/** What is missing from this record, in the order a rep would care. */
function gapsOf(c) {
  const g = []
  if (!c.sport) g.push('no sport')
  if (!c.governingBody) g.push('no governing body')
  if (!c.effDate) g.push('no date')
  if (!c.level) g.push('no level')
  return g
}

/**
 * WHO WON — and when nobody has, WHY NOT.
 *
 * ⚠️ THE CHAMPION IS THE TRIGGER FOR THE SALE, not a detail on the record: a rep
 * cannot call anyone until they know who won. An empty field used to render as a
 * single grey "No champion recorded" for four opposite situations — the event
 * has not happened, nobody has looked, three searches found nothing, or no result
 * was ever published. Every verdict here comes from lib/championState.js, which
 * is pure and asserted 19 ways; this only draws.
 */
function ChampionCell({ c }) {
  const st = championState(c)
  const all = championList(c)
  const field = championField(c)
  const { partial } = championName(c)
  /* ⚠️ MEASURED, NOT ASSUMED. Whether this event's field is worth calling comes
   * from order history; absent that measurement it stays secondary. */
  const emph = fieldEmphasis(c)

  if (st.kind === 'crowned') {
    return (
      <span className={styles.champCrowned}>
        <span className={styles.champLine}>
          <span className={styles.crown} aria-hidden="true">♛</span>
          {/* ⚠️ EVERY CHAMPION, NOT THE FIRST. Wrestling crowns one per weight
              class plus a team title; a horse show crowns ~150 class titles. A
              UI that assumes one hides the rest. */}
          <span className={styles.champNames}>
            {all.map((n) => <span key={n} className={styles.champName}>{n}</span>)}
          </span>
        </span>
        {/* ⚠️ FLAGGED, NEVER COMPLETED. A stored name that looks like a fragment
            says so; nothing appends "HS" to it, because inventing the rest of an
            institution's name is fabricating a customer. */}
        {partial && (
          <span className={styles.champPartial}>
            Stored name looks incomplete — confirm at the source before quoting it.
          </span>
        )}
        {/* ⚠️ A CROWN IS A TRANSACTION. 63 crowns are recorded with no source at
            all, and an unsourced claim must not look like a verified one. */}
        {st.sourced
          ? <span className={styles.champSourced}>Sourced</span>
          : <span className={styles.champUnsourced}>No source on the record</span>}
        {/* ⚠️ THE FIELD IS SECONDARY UNLESS THE DATA SAYS OTHERWISE.
            2.84.0 promoted the runner-up on every crowned record on the theory
            that a bracket is 64 customers. Brian withdrew that: "Normally runner
            ups aren't getting them." At an ordinary state final the runner-up
            does not buy, and a UI that implies otherwise sends reps after calls
            that do not convert. The data is kept, the claim is dropped, and only
            `fieldEmphasis` — reading measured order history — may promote it. */}
        {field.length > 1 && (
          <span className={emph.promote ? styles.champFieldLead : styles.champField}>
            {field.slice(1).map((f) => (
              <span key={f.place} className={styles.champFieldItem}>
                {f.place}: {f.who}
              </span>
            ))}
            <span className={emph.promote ? styles.champFieldWhy : styles.champFieldAside}>
              {emph.note}
            </span>
          </span>
        )}
      </span>
    )
  }

  /* ⚠️ FIVE OPPOSITE SITUATIONS, FIVE DIFFERENT THINGS TO DO ABOUT THEM. They
     used to share one grey "No champion recorded".
     ⚠️ A DECISION DOES NOT GET STYLED AS AN OPEN POSITION. "Not yet contested"
     is neutral, a withdrawn crown is a standing instruction, and the two
     genuinely-open cases are work. Three treatments, not one grey line. */
  const tone = st.kind === 'not-yet'
    ? styles.champNotYet
    : st.kind === 'prior-finding' || st.kind === 'no-result'
      ? styles.champDecided
      : styles.champBlank
  return (
    <span className={tone}>
      <span className={styles.champHeadline}>{st.headline}</span>
      <span className={styles.champWhy}>{st.detail}</span>
      {st.escalate && (
        <span className={styles.champEscalate}>
          Another search is not the next step — call the organisation.
        </span>
      )}
      {/* ⚠️ A LOCK IS LEGIBLE BEFORE ANY CONTROL IT WOULD VETO. `champion_locked`
          has been on every page this app reads and nothing looked at it; the
          only way to discover one was to press a button and read the refusal. */}
      {st.locked && (
        <span className={styles.champLocked}>
          A person has locked the champion on this record — it is not open for a hunt.
        </span>
      )}
    </span>
  )
}

/** A date a rep can plan on, and one they cannot, must not look the same. */
function DateCell({ c }) {
  if (!c.effDate) return <span className={styles.gap}>No date recorded</span>
  const projected = c.dateConfidence !== 'confirmed'
  return (
    <span className={projected ? styles.dateProjected : styles.dateConfirmed}>
      {formatDate(c.effDate)}
      <span className={styles.dateTag}>
        {c.dateConfidence === 'confirmed' ? 'confirmed' : `${c.dateConfidence || 'unsourced'} — not a sourced date`}
      </span>
    </span>
  )
}

/**
 * ⚠️ A ROW BUDGET, NOT A COLLAPSED-BY-DEFAULT PANEL.
 *
 * Every group started closed, so selecting a region showed a list of governing
 * body names and nothing else — the champion, which is the trigger for the
 * whole sale, sat two taps deep behind a disclosure. Brian asked to "see what
 * championships there are", and a header count is not that.
 *
 * But opening everything is not the answer either: CIF alone carries ~1,138
 * records (per Brian's brief of 2026-10-01), and rendering all of them into a
 * phone would be its own defect. So groups open in order until this many rows
 * are on screen, and ⚠️ WHEN ANY GROUP IS LEFT CLOSED THE PANEL SAYS SO — a
 * disclosure nobody is told about is indistinguishable from missing data.
 */
const AUTO_OPEN_ROW_BUDGET = 60

export default function StatePanel({ code, items, onOpen, onClear, noun = 'championship', nounPlural = 'championships' }) {
  /* `null` means "nobody has touched this yet, use the budget". An explicit Set
   * means the person has made a choice, and their choice is never overridden. */
  const [openBodies, setOpenBodies] = useState(null)

  const inState = useMemo(
    () => items.filter((c) => c.state === code),
    [items, code]
  )

  const groups = useMemo(() => {
    const by = new Map()
    for (const c of inState) {
      const k = c.governingBody || UNSTATED
      if (!by.has(k)) by.set(k, [])
      by.get(k).push(c)
    }
    return [...by.entries()]
      .map(([body, rows]) => ({
        body,
        rows: rows.slice().sort((a, b) => {
          /* Soonest first; undated last but NEVER dropped. */
          if (!a.effDate && !b.effDate) return String(a.name).localeCompare(String(b.name))
          if (!a.effDate) return 1
          if (!b.effDate) return -1
          return a.effDate - b.effDate
        }),
        unassigned: rows.filter((c) => !ownerName(c)).length,
        /* ⚠️ COUNTED BY THE SAME FUNCTION THAT DRAWS THE ROW. `c.crowned` is the
         * STATUS FLAG (championStatus === 'Champion Crowned'), which answers a
         * different question from "is there a champion's name on this record" —
         * championships.js says so in as many words. Counting the header from
         * the flag while the rows render from the name is how a group says
         * "12 crowned" above twelve rows that show no champion. */
        crowned: rows.filter((c) => championState(c).kind === 'crowned').length,
      }))
      .sort((a, b) => b.rows.length - a.rows.length || a.body.localeCompare(b.body))
  }, [inState])

  const listed = groups.reduce((n, g) => n + g.rows.length, 0)
  const reconciled = listed === inState.length
  const unassigned = inState.filter((c) => !ownerName(c)).length
  const incomplete = inState.filter((c) => gapsOf(c).length > 0).length

  /**
   * ⚠️ THE UNCROWNED COUNT IS THE WORK, NOT AN EMBARRASSMENT. Brian's rule:
   * "Do not quietly filter uncrowned records out." So it is stated here, split
   * by WHY, and every one of those records still renders in the list below.
   *
   * Scale, as STATED IN BRIAN'S BRIEF of 2026-10-01 and NOT independently
   * measured by this session: ~8,743 records uncrowned, with all 1,414 cheer
   * titles among them. Attributed rather than dated on purpose — quoting a
   * figure as though this session read it is how "3 of 15,082" got reported as
   * the state of production.
   *
   * ⚠️ AND THE FLAG/NAME DISAGREEMENT IS SURFACED, NOT RESOLVED. A record whose
   * status says "Champion Crowned" while carrying no champion name is a data
   * defect; the UI reports the conflict instead of picking a winner, because
   * picking one hides it forever.
   */
  const champCensus = useMemo(() => {
    const t = { crowned: 0, notYet: 0, neverSearched: 0, searchedNothing: 0, noResult: 0, withdrawn: 0, escalate: 0, unsourced: 0, flagConflict: 0 }
    for (const c of inState) {
      const st = championState(c)
      if (st.kind === 'crowned') { t.crowned++; if (!st.sourced) t.unsourced++ }
      else if (st.kind === 'not-yet') t.notYet++
      else if (st.kind === 'never-searched') t.neverSearched++
      else if (st.kind === 'no-result') t.noResult++
      /* ⚠️ A WITHDRAWN CROWN IS NOT "SEARCHED WITHOUT A RESULT". Counting it
       * there would put a deliberate retraction into the hunt workload — the
       * precise mistake the retraction narrative exists to prevent. */
      else if (st.kind === 'prior-finding') t.withdrawn++
      else t.searchedNothing++
      if (st.escalate) t.escalate++
      if (Boolean(c.crowned) !== (st.kind === 'crowned')) t.flagConflict++
    }
    return t
  }, [inState])
  const uncrowned = inState.length - champCensus.crowned
  const champBreakSum = champCensus.notYet + champCensus.neverSearched
    + champCensus.searchedNothing + champCensus.withdrawn + champCensus.noResult

  /* The budget, applied only while nobody has chosen. Groups are already sorted
   * largest-first, so this opens the biggest group a phone can take and stops. */
  const autoOpen = useMemo(() => {
    const s = new Set()
    let rows = 0
    for (const g of groups) {
      if (rows && rows + g.rows.length > AUTO_OPEN_ROW_BUDGET) continue
      s.add(g.body)
      rows += g.rows.length
      if (rows >= AUTO_OPEN_ROW_BUDGET) break
    }
    return s
  }, [groups])
  const effectiveOpen = openBodies ?? autoOpen
  const closedGroups = groups.filter((g) => !effectiveOpen.has(g.body))
  const closedRows = closedGroups.reduce((n, g) => n + g.rows.length, 0)

  const toggle = (body) => setOpenBodies((prev) => {
    const next = new Set(prev ?? autoOpen)
    if (next.has(body)) next.delete(body); else next.add(body)
    return next
  })
  const openAll = () => setOpenBodies(new Set(groups.map((g) => g.body)))

  if (!code) return null

  return (
    <section className={styles.panel} aria-label={`${stateName(code)} ${nounPlural}`}>
      <header className={styles.head}>
        <div>
          <div className="eyebrow">Selected region</div>
          <h2 className={styles.title}>{code === NATIONAL ? 'National / unspecified' : stateName(code)}</h2>
        </div>
        <button type="button" className="btn" onClick={onClear}>Clear selection</button>
      </header>

      {!inState.length && (
        <p className={styles.note}>
          <strong>No {nounPlural} in view here.</strong> This is a count of what the current
          filters admit, not a statement that the region holds none.
        </p>
      )}

      {inState.length > 0 && (
        <>
          <div className={styles.stats}>
            <span><strong>{inState.length.toLocaleString()}</strong> {inState.length === 1 ? noun : nounPlural}</span>
            <span><strong>{groups.length}</strong> governing {groups.length === 1 ? 'body' : 'bodies'}</span>
            <span><strong>{unassigned.toLocaleString()}</strong> with no rep assigned</span>
            {incomplete > 0 && <span><strong>{incomplete.toLocaleString()}</strong> incomplete — shown, not hidden</span>}
          </div>

          {/* ⚠️ WHO WON, AS A COUNT. The champion is the trigger for the sale, so
              the first number in this region is how many of them a rep can
              actually act on — and the second is how many they cannot, broken
              down by what is standing in the way. */}
          <div className={styles.stats}>
            <span><strong>{champCensus.crowned.toLocaleString()}</strong> crowned</span>
            <span><strong>{uncrowned.toLocaleString()}</strong> without a single confirmed champion</span>
            {champCensus.unsourced > 0 && (
              <span><strong>{champCensus.unsourced.toLocaleString()}</strong> crowned with no source</span>
            )}
          </div>
          {uncrowned > 0 && (
            <p className={styles.champBreak}>
              Of the <strong>{uncrowned.toLocaleString()}</strong> without a single confirmed champion:{' '}
              <strong>{champCensus.notYet.toLocaleString()}</strong> with a future event date,{' '}
              <strong>{champCensus.neverSearched.toLocaleString()}</strong> never searched,{' '}
              <strong>{champCensus.searchedNothing.toLocaleString()}</strong> searched without a result,{' '}
              <strong>{champCensus.withdrawn.toLocaleString()}</strong> carrying a prior, unresolved or withdrawn
              finding, <strong>{champCensus.noResult.toLocaleString()}</strong> settled as never published.
              {champCensus.escalate > 0 && (
                <>
                  {' '}
                  <strong>{champCensus.escalate.toLocaleString()}</strong> of them have been searched
                  three or more times and should be taken to the organisation rather than searched again.
                </>
              )}
              {' '}Every one of them is in the list below — none are filtered out.
              {/* ⚠️ THE BREAKDOWN RECONCILES, OUT LOUD. Five reasons that do not
                  sum to the total means a sixth kind exists and is being
                  swallowed — the same silent-drop defect as a list that loses a
                  row, and the reason the group count is reconciled above. */}
              {champBreakSum !== uncrowned && (
                <>
                  {' '}
                  <strong className={styles.champBreakFail}>
                    These five figures sum to {champBreakSum.toLocaleString()}, not{' '}
                    {uncrowned.toLocaleString()} — do not trust this breakdown until that is explained.
                  </strong>
                </>
              )}
            </p>
          )}
          {/* ⚠️ REPORTED, NOT RESOLVED. The status flag and the champion name
              disagreeing is a defect at the source, and the UI must not quietly
              choose one of them. */}
          {champCensus.flagConflict > 0 && (
            <p className={styles.champConflict} role="alert">
              <strong>{champCensus.flagConflict.toLocaleString()}</strong>{' '}
              {champCensus.flagConflict === 1 ? 'record disagrees' : 'records disagree'} with
              {' '}themselves: the champion status flag and the champion name on the record do not
              match. Nothing here has been corrected — this needs fixing at the source.
            </p>
          )}

          {/* ⭐ THE RECONCILIATION. Stated on every render, pass or fail. */}
          <p className={reconciled ? styles.reconciled : styles.reconcileFail} role={reconciled ? undefined : 'alert'}>
            {reconciled
              ? `Every one of the ${inState.length.toLocaleString()} ${nounPlural} here is in exactly one group below — nothing has been dropped from this list.`
              : `RECONCILIATION FAILED. ${inState.length.toLocaleString()} in this region, ${listed.toLocaleString()} listed. Do not trust this list until that is explained.`}
          </p>

          {/* ⚠️ A CLOSED GROUP IS DECLARED, NOT SILENT. Otherwise a collapsed
              governing body is indistinguishable from one that holds nothing,
              which is the same failure as filtering a record out. */}
          {closedRows > 0 && (
            <p className={styles.champBreak}>
              {closedGroups.length === 1
                ? `1 governing body below is collapsed, holding ${closedRows.toLocaleString()} ${closedRows === 1 ? noun : nounPlural}.`
                : `${closedGroups.length} governing bodies below are collapsed, holding ${closedRows.toLocaleString()} ${nounPlural} between them.`}
              {' '}They are counted in every figure above — collapsed, not excluded.{' '}
              <button type="button" className={styles.openAll} onClick={openAll}>
                Open all {groups.length} governing {groups.length === 1 ? 'body' : 'bodies'}
              </button>
            </p>
          )}

          {groups.map((g) => {
            const open = effectiveOpen.has(g.body)
            return (
              <div key={g.body} className={styles.group}>
                <button
                  type="button"
                  className={styles.groupHead}
                  aria-expanded={open}
                  onClick={() => toggle(g.body)}
                >
                  <span className={styles.groupName}>{g.body}</span>
                  <span className={styles.groupMeta}>
                    {g.rows.length.toLocaleString()} {g.rows.length === 1 ? noun : nounPlural}
                    {/* ⚠️ ALWAYS STATED, INCLUDING ZERO. This read `g.crowned > 0 &&`,
                        so a governing body with nothing crowned said nothing at all,
                        and the body with the most work to do was the one that looked
                        cleanest. Cheer is the case that makes it matter: per Brian's
                        brief of 2026-10-01, every cheer title is uncrowned. */}
                    {` · ${g.crowned.toLocaleString()} of ${g.rows.length.toLocaleString()} crowned`}
                    {g.unassigned > 0 && ` · ${g.unassigned.toLocaleString()} with no rep assigned`}
                  </span>
                  <span className={styles.chev} aria-hidden="true">{open ? '−' : '+'}</span>
                </button>

                {open && (
                  <ul className={styles.rows}>
                    {g.rows.map((c) => {
                      const gaps = gapsOf(c)
                      const rep = ownerName(c)
                      return (
                        <li key={c.id} className={styles.row}>
                          {/* ⚠️ THE WHOLE ROW IS THE CONTROL. A list row with no
                              onClick anywhere in its subtree has shipped here
                              before; check-clickable.mjs exists because of it. */}
                          <button
                            type="button"
                            className={styles.rowBtn}
                            onClick={() => onOpen(c)}
                            aria-label={`Open ${c.name}`}
                          >
                            <span className={styles.rowName}>{c.name}</span>
                            <span className={styles.rowMeta}>
                              <span className={styles.metaBody}>{c.governingBody || UNSTATED}</span>
                              <span>{c.sport || <em className={styles.gap}>sport not recorded</em>}</span>
                              {c.level && <span>{c.level}</span>}
                              {c.division && <span>{c.division}</span>}
                            </span>
                            {/* ⚠️ THE CHAMPION IS NOT THE FOURTH FACT IN A WRAPPED ROW.
                                It is the trigger for the sale, so it reads
                                directly under the championship name, in its own
                                block, at full width. */}
                            <RecordedChampion record={c} />
                            <ChampionCell c={c} />
                            <span className={styles.rowFacts}>
                              <DateCell c={c} />
                              <span className={styles.fact}>
                                {rep || <span className={styles.gap}>No rep assigned</span>}
                                <SchoolTeamCoverage record={c}/>
                              </span>
                              <span className={styles.fact}>
                                {c.value
                                  ? `$${Math.round(c.value).toLocaleString('en-US')} recorded`
                                  : <span className={styles.gap}>No amount recorded</span>}
                              </span>
                              <span className={styles.stage} style={{ color: pursuitMeta(c.pursuit).color }}>
                                {pursuitMeta(c.pursuit).label}
                              </span>
                            </span>
                            {gaps.length > 0 && (
                              <span className={styles.gaps}>
                                Incomplete: {gaps.join(' · ')} — shown here because filtering on a blank
                                field is how a sport becomes invisible.
                              </span>
                            )}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            )
          })}
        </>
      )}
    </section>
  )
}
